import { lazy, Suspense, type ComponentType } from 'react'
import { Spinner } from '../components/ui/Spinner'
import { isChunkLoadError } from './chunkLoadError'
import { markAssetSnapshot, recordAssetsSince, reloadWithoutCachedShell } from './reloadFresh'

// Cache for preloaded modules
const preloadCache = new Set<() => Promise<unknown>>()

const CHUNK_RELOAD_KEY = 'lazy_chunk_reload_at'
// A stale shell fails again within seconds of reloading; a later deploy in the same
// session is minutes away. Cooling down between attempts allows the second case to
// recover while stopping the first from looping.
const CHUNK_RELOAD_COOLDOWN_MS = 30_000

/** Records the attempt and reports whether the caller may reload now. */
function claimReloadAttempt(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY))
    if (last && Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return false
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
    return true
  } catch {
    // sessionStorage unavailable (private mode / blocked): without it we cannot tell a
    // first failure from a looping one, so fall through to the ErrorBoundary instead.
    return false
  }
}

/**
 * Wrapper for lazy() that on chunk load failure (e.g. after deploy) reloads the page once
 * so the user gets the latest assets. Uses sessionStorage to avoid reload loops.
 *
 * Safe for panels/drawers opened mid-session, not just routes: the reload is only reached
 * when the alternative is the ErrorBoundary, and AgentStudioPage caches unsaved graph state
 * on beforeunload.
 */
export function lazyWithChunkRetry<T extends ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  importFunc: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    // Bracket the import so a failure can name the chunks it fetched; recovery then
    // re-downloads just those instead of every asset on the page.
    const assetMark = markAssetSnapshot()
    try {
      return await importFunc()
    } catch (err) {
      recordAssetsSince(assetMark)
      if (isChunkLoadError(err) && claimReloadAttempt()) {
        // Bypass the HTTP cache: the chunk may be missing (renamed by a deploy) or
        // cached corrupt, and a plain reload cannot fix the second case.
        void reloadWithoutCachedShell()
        return new Promise(() => {}) // never resolves so component stays in suspense
      }
      throw err
    }
  })
}

/**
 * Lazy load a component with a loading fallback
 *
 * @example
 * ```tsx
 * const ConnectorModal = lazyLoad(() => import('./ConnectorModal'));
 *
 * // Usage
 * {showModal && <ConnectorModal isOpen={true} onClose={handleClose} />}
 * ```
 */
export function lazyLoad<T extends ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  importFunc: () => Promise<{ default: T }>,
  fallback: React.ReactNode = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px',
      }}
    >
      <Spinner size="md" />
    </div>
  )
) {
  const LazyComponent = lazy(importFunc)

  return (props: React.ComponentProps<T>) => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  )
}

/**
 * Lazy load a modal component with automatic preloading on idle
 * Modals are perfect candidates for lazy loading as they're not always visible
 *
 * @param importFunc - The import function for the modal
 * @param preloadOnIdle - Whether to preload the modal during browser idle time (default: true)
 *
 * @example
 * ```tsx
 * const ConnectorModal = lazyLoadModal(() => import('./ConnectorModal'));
 * ```
 */
export function lazyLoadModal<T extends ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  importFunc: () => Promise<{ default: T }>,
  preloadOnIdle: boolean = true
) {
  // Preload during idle time if enabled
  if (preloadOnIdle && !preloadCache.has(importFunc)) {
    preloadCache.add(importFunc)

    // Use requestIdleCallback if available, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          importFunc().catch(() => {
            // Silently fail - component will be loaded when actually needed
          })
        },
        { timeout: 2000 }
      )
    } else {
      setTimeout(() => {
        importFunc().catch(() => {
          // Silently fail
        })
      }, 100)
    }
  }

  // CRITICAL: Use null fallback for modals to avoid DOM wrapper interference
  // Modals render their own overlays and any wrapper div breaks positioning
  return lazyLoad(importFunc, null)
}

/**
 * Preload a lazy-loaded component
 * Useful for prefetching components before they're needed
 *
 * @example
 * ```tsx
 * const ConnectorModal = lazyLoadModal(() => import('./ConnectorModal'));
 *
 * // Preload on hover
 * <button onMouseEnter={() => preload(() => import('./ConnectorModal'))}>
 *   Add Connector
 * </button>
 * ```
 */
export function preload<T extends ComponentType<any>>(importFunc: () => Promise<{ default: T }>) { // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!preloadCache.has(importFunc)) {
    preloadCache.add(importFunc)
    importFunc().catch(() => {
      // Silently fail
    })
  }
}

/**
 * Create a preloadable button that preloads a component on hover
 *
 * @example
 * ```tsx
 * const preloadConnectorModal = createPreloadTrigger(() => import('./ConnectorModal'));
 *
 * <button {...preloadConnectorModal()}>Add Connector</button>
 * ```
 */
export function createPreloadTrigger<T extends ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  importFunc: () => Promise<{ default: T }>
) {
  return () => ({
    onMouseEnter: () => preload(importFunc),
    onFocus: () => preload(importFunc),
  })
}
